import React, { PropsWithChildren, useCallback, useContext, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import { NavWrapper } from 'components/OfferPage/styles'
import TextButton from 'LuxKit/Button/TextButton'
import WalledContent from 'components/Common/WalledContent'
import BodyText from 'LuxKit/Typography/BodyText'
import useBedbankRates from 'hooks/useBedbankRates'
import useBedbankOfferSell from 'hooks/useBedbankOfferSell'
import { getDisplayTotalPrice, getDisplayTotalPricePerNight } from 'lib/hotels/bedbankUtils'
import { useHeaderStateObserver } from 'contexts/HeaderStateContext'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { pluralizeToString } from 'lib/string/pluralize'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import Group from 'components/utils/Group'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import { getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import config from 'constants/config'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import useBedbankFlightPrice from 'hooks/OfferPage/useBedbankFlightPrice'

const NavLayoutContainer = styled(LayoutContainer)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  row-gap: ${rem(8)};
`

const StyledPackageOptionsButton = styled(TextButton)`
  margin-left: auto;
`

const PriceSummarySection = styled(Group)`
  width: 100%;
  flex-shrink: 0;

  ${mediaQueryUp.tablet} {
    width: auto;
    padding-left: ${rem(12)};
    margin-left: auto;
  }
`

interface Props {
  duration: number
  onViewPackageOptions?: () => void
  offer: App.BedbankOffer
  rooms: Array<App.Occupants>
  checkInDate?: string
  checkOutDate?: string
  flightPrice?: number
  flightsEnabled: boolean
  luxPlusLPPEnabled: boolean
  airport?: App.Airport
}

function BedbankOfferHeader(props: PropsWithChildren<Props>) {
  const {
    children,
    onViewPackageOptions,
    offer,
    rooms,
    checkInDate,
    checkOutDate,
    duration,
    flightsEnabled,
    luxPlusLPPEnabled,
    airport,
  } = props

  const ref = useRef<HTMLDivElement>(null)
  useHeaderStateObserver('bedbankOfferHeader', ref)
  const [{
    hotelOnlyRates,
    hotelBundleRates,
  }, fetching] = useBedbankRates(offer.id, rooms, checkInDate, checkOutDate)

  const sell = useBedbankOfferSell(offer, duration, rooms)
  const flightPrice = useBedbankFlightPrice(offer, {
    rooms,
    startDate: checkInDate,
    endDate: checkOutDate,
    originAirportCode: airport?.code,
  })

  const hotelPrice = hotelOnlyRates[0] ? hotelOnlyRates[0].totals.inclusive : sell?.price ?? 0
  const hotelMemberPrice = hotelOnlyRates[0] ? hotelOnlyRates[0].totals.memberInclusive ?? 0 : 0
  const bundlePrice = hotelBundleRates[0]?.totals.inclusive || hotelPrice
  const hotelNights = hotelOnlyRates[0]?.nights ?? sell?.los
  const propertyFees = hotelOnlyRates[0]?.totals?.propertyFees ?? sell?.propertyFees ?? 0
  const bundlePropertyFees = hotelBundleRates[0]?.totals.propertyFees || propertyFees

  const datesSelected = !!(checkInDate && checkOutDate)
  const hasRates = hotelOnlyRates.length > 0
  const showSummary = (datesSelected && hasRates) || (!datesSelected && sell)
  const isSoldOut = hotelOnlyRates.length == 0 && datesSelected
  const baseSaleUnit = 'room'
  const saleUnit = offer.displayPricingAsPerPerson ? 'person' : baseSaleUnit

  const promoteAsBundle = flightsEnabled && !!flightPrice && offer.promoteAsBundle
  const showOneNightPrice = !offer.displayPricingAsPerPerson && !checkInDate && !!hotelNights && hotelNights > 1
  const nightsToDisplay = showOneNightPrice ? 1 : hotelNights

  const totalBasePrices = useMemo(() => {
    return getDisplayTotalPrice({
      hotelPrice: hotelPrice + propertyFees,
      hotelMemberPrice: hotelMemberPrice > 0 ? hotelMemberPrice + propertyFees : 0,
      bundlePrice: bundlePrice + bundlePropertyFees,
      flightPrice,
      checkIn: checkInDate,
      rooms,
      displayPricingAsPerPerson: offer.displayPricingAsPerPerson,
    })
  }, [bundlePrice, bundlePropertyFees, checkInDate, flightPrice, hotelMemberPrice, hotelPrice, offer.displayPricingAsPerPerson, propertyFees, rooms])

  const totalPrices = showOneNightPrice ? getDisplayTotalPricePerNight(totalBasePrices, hotelNights) : totalBasePrices
  const offerPrice = promoteAsBundle ? totalPrices.totals.flightBundlePrice : totalPrices.totals.price
  const offerMemberPrice = promoteAsBundle ? totalPrices.totals.flightBundleMemberPrice : totalPrices.totals.memberPrice

  const showModal = useContext(ModalContext)
  const onLogin = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  return (
    <NavWrapper>
      <NavLayoutContainer>
        <div>
          {children}
        </div>
        <WalledContent
          enforced={offer.walledGarden}
          hidePricing={offer.hidePricing}
          wall={(
            <PriceSummarySection
              direction="horizontal"
              verticalAlign="center"
              horizontalAlign="space-between"
              tabletHorizontalAlign="end"
              gap={16}
            >
              <BodyText variant="medium" weight="bold">Sign up to access exclusive rates</BodyText>
              <Group direction="horizontal" gap={8} shrink={false}>
                <TextButton kind="secondary" onClick={onLogin}>
                  Log In
                </TextButton>
                <TextButton kind="primary" onClick={onLogin}>
                  Sign up<CSSBreakpoint min="tablet" as="span">&nbsp;for free</CSSBreakpoint>
                </TextButton>
              </Group>
            </PriceSummarySection>
          )}
        >
          <PriceSummarySection
            direction="horizontal"
            verticalAlign="center"
            horizontalAlign="space-between"
            tabletHorizontalAlign="end"
            gap={12}
          >
            {showSummary && !isSoldOut && !fetching && <Group direction="vertical" tabletHorizontalAlign="end">
              <PriceRowPriceCaption>
                {pluralizeToString('night', nightsToDisplay)}{promoteAsBundle && ' + flights'} from
              </PriceRowPriceCaption>
              <LuxPlusPriceGuard
                showMemberPrice={(offerMemberPrice ?? 0) > 0 && luxPlusLPPEnabled}
                fallbackPrice={<PriceRowPrice
                  data-testid="bedbank-sticky-header-price"
                  size="S"
                  price={offerPrice}
                  saleUnit={saleUnit}
                />}
              >
                <PriceRowLuxPlusPrice
                  size="S"
                  memberPrice={offerMemberPrice ?? 0}
                  saleUnit={saleUnit}
                  logoPlacement="leading"
                />
              </LuxPlusPriceGuard>
            </Group>}
            {isSoldOut && !fetching && <>
              <BodyText
                variant="large"
                weight="bold"
              >
                Sold out on your dates
              </BodyText>
              <TextButton kind="primary" to="/hotels">
                Browse all hotel offers
              </TextButton>
            </>}
            {!isSoldOut && <StyledPackageOptionsButton kind="primary" onClick={onViewPackageOptions}>
              <CSSBreakpoint max="tablet">{datesSelected ? 'View options' : 'Check availability'}</CSSBreakpoint>
              <CSSBreakpoint min="desktop">{datesSelected ? `View ${config.brandConfig.travelSynonym} options` : 'Check availability'}</CSSBreakpoint>
            </StyledPackageOptionsButton>}
          </PriceSummarySection>
        </WalledContent>
      </NavLayoutContainer>
    </NavWrapper>
  )
}

export default withOfferPageAndReduxState((offerState, state) => ({
  offer: offerState.bedbankOffer!,
  rooms: offerState.rooms,
  duration: offerState.duration,
  checkInDate: offerState.checkInDate,
  checkOutDate: offerState.checkOutDate,
  airport: offerState.airport,
  open: !!offerState.activeFilter,
  luxPlusLPPEnabled: getIsLuxPlusLPPEnabled(state),
}), BedbankOfferHeader, preventRenderWithOpenFilter)
